/**
 * useRegulatoryLines — shared hook for building multi-standard regulatory
 * reference lines from analytical timeseries data.
 *
 * Extracts regulatory_limits from wells → analytes, deduplicates by
 * (analyte, library), respects the global visibility filter, and
 * returns an array of line specs ready for <RegulatoryLines />.
 *
 * As a side effect, this hook also reports the set of libraries that HAVE
 * at least one line on the currently-visible analytes up to the dataVault
 * store (currentChartRegulatoryLibraries). FilterRibbon reads that slice
 * to decide which STANDARDS chips to render, so chips only appear for
 * libraries that actually correspond to a rendered line — clicking a chip
 * is guaranteed to have a visible effect.
 */
import { useMemo, useEffect } from 'react';
import type { AnalyticalTimeseriesResponse, RegulatoryLimitInfo } from '@/api/dataVault';
import { useDataVaultStore } from '@/store/dataVault';

export interface RegulatoryLine {
  key: string;
  analyteCode: string;
  analyteName: string;
  library: string;
  value: number;  // mg/L
  color: string;
  style: 'solid' | 'dashed' | 'dotted';
  label: string;
}

/**
 * Build regulatory lines for visible analytes from timeseries data.
 *
 * @param data - AnalyticalTimeseriesResponse (wells with analytes)
 * @param visibleAnalytes - Set of visible analyte codes
 * @returns { lines, allLibraries } where lines respects visibility filter
 */
export function useRegulatoryLines(
  data: AnalyticalTimeseriesResponse | null,
  visibleAnalytes: Set<string>,
) {
  const hiddenLibraries = useDataVaultStore((s) => s.hiddenRegulatoryLibraries);

  // Collect all regulatory limits from all wells, deduped by (analyte, library)
  const { lines, allLibraries } = useMemo(() => {
    if (!data?.wells) return { lines: [], allLibraries: new Set<string>() };

    const seen = new Set<string>();
    const result: RegulatoryLine[] = [];
    const libs = new Set<string>();

    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!visibleAnalytes.has(code)) continue;

        // Multi-standard: regulatory_limits dict
        if (analyte.regulatory_limits) {
          for (const [lib, info] of Object.entries(analyte.regulatory_limits)) {
            const key = `${code}:${lib}`;
            if (seen.has(key)) continue;
            seen.add(key);
            libs.add(lib);
            result.push({
              key,
              analyteCode: code,
              analyteName: analyte.compound,
              library: lib,
              value: info.value,
              color: info.color,
              style: info.style,
              label: info.label,
            });
          }
        }

        // Fallback: single mcl field (sites without regulatory config)
        if (!analyte.regulatory_limits && analyte.mcl != null) {
          const key = `${code}:MCL`;
          if (seen.has(key)) continue;
          seen.add(key);
          libs.add('MCL');
          result.push({
            key,
            analyteCode: code,
            analyteName: analyte.compound,
            library: 'MCL',
            value: analyte.mcl,
            color: '#ef4444',
            style: 'dashed',
            label: 'Reg Limit',
          });
        }
      }
    }

    return { lines: result, allLibraries: libs };
  }, [data, visibleAnalytes]);

  // Filter by global visibility
  const visibleLines = useMemo(
    () => lines.filter((l) => !hiddenLibraries.has(l.library)),
    [lines, hiddenLibraries],
  );

  // Build structured metadata for libraries with at least one rendered line
  // on currently-visible analytes (ignoring hide-state — we want the chip to
  // render even when hidden, it's how you toggle it back on). Deduped by key.
  const currentLibraries = useMemo(() => {
    const byKey = new Map<string, { key: string; label: string; color: string }>();
    for (const line of lines) {
      if (byKey.has(line.library)) continue;
      byKey.set(line.library, { key: line.library, label: line.label, color: line.color });
    }
    return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [lines]);

  // Report to the store so FilterRibbon can render chips for only the
  // libraries that actually appear as rendered lines on THIS chart.
  const setCurrent = useDataVaultStore((s) => s.setCurrentChartRegulatoryLibraries);
  useEffect(() => {
    setCurrent(currentLibraries);
    return () => setCurrent([]);
  }, [currentLibraries, setCurrent]);

  return { lines: visibleLines, allLibraries, currentLibraries };
}

export interface AvailableLibrary {
  key: string;
  label: string;
  color: string;
}

/**
 * Scan the full timeseries response for every regulatory library present,
 * independent of per-chart visibility state. Returns structured metadata
 * sourced entirely from the backend so the frontend never needs to know
 * library names, colors, or labels ahead of time.
 */
export function useAvailableRegulatoryLibraries(
  data: AnalyticalTimeseriesResponse | null,
): AvailableLibrary[] {
  return useMemo(() => {
    if (!data?.wells) return [];

    const byKey = new Map<string, AvailableLibrary>();

    for (const well of data.wells) {
      for (const analyte of Object.values(well.analytes)) {
        if (analyte.regulatory_limits) {
          for (const [lib, info] of Object.entries(analyte.regulatory_limits)) {
            if (byKey.has(lib)) continue;
            byKey.set(lib, { key: lib, label: info.label, color: info.color });
          }
        } else if (analyte.mcl != null && !byKey.has('MCL')) {
          byKey.set('MCL', { key: 'MCL', label: 'Reg Limit', color: '#ef4444' });
        }
      }
    }

    return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);
}
